import React, { useState, KeyboardEvent, ChangeEvent } from 'react';
import { Send, Loader2 } from 'lucide-react';
import './MessageInput.css';

interface MessageInputProps {
  onSendMessage: (content: string) => Promise<void>;
  disabled?: boolean;
}

const MAX_MESSAGE_LENGTH = 2000;

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, disabled = false }) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isSending || disabled) {
      return;
    }

    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      setError(`Message is too long (max ${MAX_MESSAGE_LENGTH} characters)`);
      return;
    }

    setError(null);
    setIsSending(true);

    try {
      await onSendMessage(trimmedMessage);
      setMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (error) {
      setError(null);
    }
  };

  const isMessageValid = message.trim().length > 0 && message.trim().length <= MAX_MESSAGE_LENGTH;
  const showCharCount = message.length > MAX_MESSAGE_LENGTH * 0.8;

  return (
    <div className="message-input-container">
      {error && (
        <div className="message-input-error">
          {error}
        </div>
      )}
      <div className="message-input-wrapper">
        <textarea
          className="message-input-field"
          placeholder="Type a message..."
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isSending || disabled}
          rows={1}
          maxLength={MAX_MESSAGE_LENGTH}
        />
        {showCharCount && (
          <div className={`message-char-count ${message.length > MAX_MESSAGE_LENGTH ? 'over-limit' : ''}`}>
            {message.length}/{MAX_MESSAGE_LENGTH}
          </div>
        )}
        <button
          className="message-send-button"
          onClick={handleSend}
          disabled={!isMessageValid || isSending || disabled}
          title="Send message"
        >
          {isSending ? (
            <Loader2 size={20} className="spinner" />
          ) : (
            <Send size={20} />
          )}
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
